import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Alert, Button, Status, Tabs, Typography, type ITabItem } from 'shared/ui'
import { LINK_RCS_GUIDE } from 'shared/constants/links'
import { rcsClientStatusConfig } from 'entities/Compliance'
import { isRcsComplianceDataEmpty } from 'entities/Rcs'
import { RcsWizardSteps } from 'pages/settings/pages/rcsV2/route/type'
import { RcsBadge } from 'pages/settings/pages/rcsV2/ui'
import { useRcsWizardContext } from 'pages/settings/pages/rcsV2/context/rcsWizardContext'
import { SenderDetailsTab } from './SenderDetailsTab'
import { ComplianceTab } from './ComplianceTab'

import styles from './styles.module.scss'

type IViewTab = 'sender' | 'compliance'

const SENDER_TAB: ITabItem<IViewTab> = { key: 'sender', name: 'Sender details' }
const COMPLIANCE_TAB: ITabItem<IViewTab> = { key: 'compliance', name: 'Compliance' }

export const RcsSubmissionView = observer(() => {
  const { submission, complianceStatus, navigationStore } = useRcsWizardContext()
  const [activeTab, setActiveTab] = useState<IViewTab>('sender')

  if (!submission) return null

  const { data: complianceData } = submission
  const hasComplianceData = !isRcsComplianceDataEmpty(complianceData)
  const tabs = hasComplianceData ? [SENDER_TAB, COMPLIANCE_TAB] : [SENDER_TAB]
  const isRejected = complianceStatus === 'failed' || complianceStatus === 'rejected'

  return (
    <section className={styles.view}>
      <div className={styles.header}>
        <RcsBadge />
        <div className={styles.headerMain}>
          <Typography variant='heading-lg' ariaLabel='RcsSubmissionView_name'>
            {submission.name}
          </Typography>
          <div className={styles.headerActions}>
            <Button
              tag='a'
              href={LINK_RCS_GUIDE}
              target='_blank'
              rel='noreferrer'
              text='Help guide'
              size='medium'
              contained='secondary'
              iconProps={{ icon: 'info' }}
              className={styles.helpGuide}
            />
            {isRejected && (
              <Button
                text='Edit'
                size='medium'
                onClick={() => navigationStore.goToStep(RcsWizardSteps.senderDetails)}
              />
            )}
          </div>
        </div>
      </div>

      {isRejected && (
        <Alert
          marginBottom={20}
          ariaLabel='RcsSubmissionView_rejectedAlert'
          item={{
            type: 'error',
            title: 'Your RCS application was rejected.',
            desc: submission.failed_reason,
          }}
        />
      )}

      <Tabs
        tabs={tabs}
        activeTabKey={activeTab}
        handleSelectTab={(tab) => setActiveTab(tab.key)}
        variant='simpleWithoutBorder'
        tabsClassName={styles.tabs}
        tabClassName={tabs.length === 1 ? styles.singleTab : undefined}
      />

      <div className={styles.card}>
        {complianceStatus && (
          <div className={styles.statusRow}>
            <Typography variant='heading-sm' ariaLabel='RcsSubmissionView_statusTitle'>
              Status
            </Typography>
            <Status {...rcsClientStatusConfig[complianceStatus]} />
          </div>
        )}
        {activeTab === 'compliance' && complianceData && hasComplianceData ? (
          <ComplianceTab data={complianceData} />
        ) : (
          <SenderDetailsTab submission={submission} />
        )}
      </div>
    </section>
  )
})
